import { ConfigProvider } from 'antd';
import {
  createContext,
  memo,
  useContext,
  useState,
  useEffect,
  type FC,
  type PropsWithChildren,
  useCallback,
} from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { version } from '../../../package.json';
import routes from '../../pages';
import theme from '../../themes';
import '../../styles/index.scss';
import './index.css';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from '../../client/queryClient';
import { Provider } from 'react-redux';
import { store } from '@/store/store';
import { ModalController } from '../modals/ModalController';
import useSocket from '@/hooks/useSocket';
import { getUnreadMessages } from '@/api/chat';
import { selectUser } from '@/slice/user';
import { useAppSelector } from '@/hooks/useAppSelector';
import { Helmet } from 'react-helmet-async';
import { useAuthValidation } from '@/hooks/useAuthValidation';
import { AdminRoutes } from '@/pages/AdminRoute';
import AppRouter from '@/pages/AppRouter';
import { updateTimeZone } from '@/api/user';
import { detectTimezone } from '@/helpers/dateTimeHelper';
import EzeeAssist from '../ezee-assist/EzeeAssist';
type ContextProps = {
  app: {
    version: string;
    showWelcomeBanner: boolean;
    setShowWelcomeBanner: React.Dispatch<React.SetStateAction<boolean>>;
    unreadCount: number;
    setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
    fetchUnreadMessages: () => void;
  };
};

const index: ContextProps['app'] = {
  version,
  showWelcomeBanner: true,
  setShowWelcomeBanner: () => {},
  unreadCount: 0,
  setUnreadCount: () => {},
  fetchUnreadMessages: () => {},
};

const Context = createContext({ app: index });

const ContextProvider: FC<PropsWithChildren<ContextProps>> = ({
  children,
  ...props
}) => {
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const { onEvent, emitEvent } = useSocket();
  const user = useAppSelector(selectUser);
  const isAuthenticated = !!localStorage.getItem('accessToken');

  const fetchUnreadMessages = useCallback(async () => {
    try {
      if (!isAuthenticated) return;
      const { unreadCount } = await getUnreadMessages(user.id);
      setUnreadCount(unreadCount);
    } catch (error) {
      console.error('Failed to fetch unread messages', error);
    }
  }, [user?.id]);

  useEffect(() => {
    // Sync the user's timezone once per login. Previously this effect
    // depended on both user.id and user.zoneInfo, so when the PATCH
    // response refreshed the user object the effect re-fired and (when
    // detectTimezone() and the stored value disagreed by even one
    // character — e.g. case or whitespace) sent the PATCH a second
    // time. The benchmark for TCX-616 observed 2 timezone PATCHes per
    // page load as a result. Drop zoneInfo from the deps so this fires
    // once per session.
    if (isAuthenticated && user?.id) {
      const timeZone = detectTimezone();
      if (!user.zoneInfo || user.zoneInfo !== timeZone) {
        try {
          updateTimeZone(timeZone);
        } catch (error) {
          console.error('TimeZone update failed', error);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    // fetchUnreadMessages();

    const pollingInterval = setInterval(fetchUnreadMessages, 10000);

    const cleanupReceiveMessage = onEvent(
      'receiveMessage',
      fetchUnreadMessages,
    );
    const cleanupReadMessage = onEvent('readMessage', fetchUnreadMessages);
    const cleanupJoinChat = onEvent('joinChat', fetchUnreadMessages);
    // Join the user's own room for meeting-link updates, and RE-join it on every
    // (re)connect. Rooms are per-connection; socket.io auto-reconnects and a
    // token refresh now forces a re-handshake (TCX-820), so without re-joining
    // on 'connect' a reconnect would silently stop meeting-link delivery.
    emitEvent('joinUserRoom');
    const cleanupUserRoomRejoin = onEvent('connect', () =>
      emitEvent('joinUserRoom'),
    );
    return () => {
      clearInterval(pollingInterval);
      cleanupReceiveMessage();
      cleanupReadMessage();
      cleanupJoinChat();
      cleanupUserRoomRejoin();
    };
  }, [user?.id]);

  props.app.showWelcomeBanner = showWelcomeBanner;
  props.app.setShowWelcomeBanner = setShowWelcomeBanner;
  props.app.unreadCount = unreadCount;
  props.app.setUnreadCount = setUnreadCount;
  props.app.fetchUnreadMessages = fetchUnreadMessages;

  return <Context.Provider value={{ ...props }}>{children}</Context.Provider>;
};

const useApp: () => ContextProps = () => useContext(Context);

const AppcuesIdentify: FC = () => {
  const user = useAppSelector((state) => state.user.user);
  useEffect(() => {
    if (window.Appcues && user?.id) {
      window.Appcues.identify(user.id, {
        name: user.firstName + ' ' + user.lastName,
        email: user.email,
      });
    }
  }, [user]);
  return null;
};

const App: FC = memo(() => (
  <Provider store={store}>
    <Helmet>
      <script
        async
        defer
        src="https://tools.luckyorange.com/core/lo.js?site-id=e521311a"
      ></script>
      <script type="text/javascript">
        {`window.AppcuesSettings = { enableURLDetection: true };`}
      </script>
      <script src="//fast.appcues.com/220921.js"></script>
    </Helmet>
    <AppcuesIdentify />
    <EzeeAssist />
    <ContextProvider app={index}>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider theme={theme}>
          <AppRouter />
          <ModalController />
        </ConfigProvider>
      </QueryClientProvider>
    </ContextProvider>
  </Provider>
));

export { useApp };

export default App;
